import { useEffect, useRef, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useWorkspaceStore,
  type StaffDetail,
  type GridKey,
} from "@/store/useWorkspaceStore";
import { getStaffDetail, setWorkStatus, getStaffConfig, updateAddress } from "@/api/staff";
import { toastSuccess, toastWarning } from "@/utils/toast";
import BottomNav from "@/components/feature/BottomNav";
import { requestLocationOnUserAction } from "@/utils/location";
import { resolveUrl } from "@/utils/workspace-url";

// ==================== 首页统计数据网格 ====================

const girdData = [
  { title: "今日接单", key: "today_order" as GridKey },
  { title: "今日完成", key: "today_finish" as GridKey },
  { title: "今日待完成", key: "today_start" as GridKey },
  { title: "已服务数量", key: "order_count" as GridKey },
  { title: "本月接单", key: "month_order" as GridKey },
  { title: "本月完成", key: "month_finish" as GridKey },
  { title: "关注数量", key: "fans_count" as GridKey },
  { title: "点赞数量", key: "praise_count" as GridKey },
];

const BRAND = "#FF6B35";

export default function WorkspaceHomePage() {
  const navigate = useNavigate();
  const staffInfo = useWorkspaceStore((s) => s.staffInfo);
  const config = useWorkspaceStore((s) => s.config);
  const loading = useWorkspaceStore((s) => s.loadingStaff);

  const setStaffInfo = useWorkspaceStore((s) => s.setStaffInfo);
  const updateField = useWorkspaceStore((s) => s.updateStaffField);
  const setLoading = useWorkspaceStore((s) => s.setLoading);
  const setConfig = useWorkspaceStore((s) => s.setConfig);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [locating, setLocating] = useState(false);

  // 获取达人信息（对应 uni-app: api.staff.getStaffDetail）
  const getStaffInfo = useCallback(async () => {
    setLoading(true);
    try {
      const info = await getStaffDetail();
      setStaffInfo(info);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "获取达人信息失败";
      console.error(msg);
    } finally {
      setLoading(false);
    }
  }, [setStaffInfo, setLoading]);

  // 加载系统配置（对应 uni-app: useStaffConfigStore）
  const loadConfig = useCallback(async () => {
    try {
      const cfg = await getStaffConfig();
      setConfig(cfg);
    } catch {
      // 配置加载失败不影响主流程
    }
  }, [setConfig]);

  // 切换接单状态（对应 uni-app: api.staff.setWork）
  const handleSwitchChange = async () => {
    // 即使 staffInfo 未加载完成也允许尝试切换
    const current = staffInfo?.work_status ?? 0;
    const next = current === 1 ? 0 : 1;
    try {
      // 先乐观更新 UI
      if (staffInfo) updateField("work_status", next);
      await setWorkStatus(next as 0 | 1);
      // 成功后提示并刷新服务端最新数据
      toastWarning(next ? "已开启接单" : "已暂停接单");
      await getStaffInfo();
    } catch (err: unknown) {
      // 回滚 UI（错误信息已在 request 层统一 toast，不再重复提示）
      if (staffInfo) updateField("work_status", current);
    }
  };

  // 获取当前位置并更新达人经纬度（对应 uni-app: getStaffLocation + resetGetLocation）
  const fetchAndUpdateLocation = useCallback(async () => {
    setLocating(true);
    try {
      const result = await requestLocationOnUserAction();
      if (!result.success || !result.location) return;

      const { latitude, longitude, formattedAddress } = result.location;
      const address = formattedAddress || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

      // 更新本地 UI 显示
      updateField("current_address", address);

      // 调用后端更新达人位置（对应 uni-app: updateAddress）
      await updateAddress({
        current_latitude: String(latitude),
        current_longitude: String(longitude),
        current_address: address,
      });

      // 刷新服务端最新数据
      await getStaffInfo();
    } catch {
      // 错误已在 request 层统一 toast
    } finally {
      setLocating(false);
    }
  }, [updateField, getStaffInfo]);

  useEffect(() => {
    getStaffInfo();
    loadConfig();
    fetchAndUpdateLocation(); // 页面加载时获取位置（对应 uni-app: getStaffInfo + getStaffLocation）

    // 定时刷新位置信息（5分钟一次，原逻辑）
    timerRef.current = setInterval(getStaffInfo, 300000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [getStaffInfo, loadConfig, fetchAndUpdateLocation]);

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+80px)] relative overflow-x-hidden"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      {/* 顶部背景图区域 */}
      <div
        className="relative pb-6 px-5"
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)",
          backgroundImage: config?.staff_home_bg
            ? `url(${config.staff_home_bg})`
            : `linear-gradient(145deg, #1A1A2E 0%, #16213E 60%, #0F3460 100%)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* 返回 + 标题 */}
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full"
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <i
              className="ri-arrow-left-line"
              style={{ fontSize: 18, color: "#000" }}
            />
          </button>
          <span className="text-base font-bold" style={{ color: "#000" }}>工作台</span>
        </div>

        {/* 接单开关 - 固定右上角 */}
        {!loading && (
          <div
            className="absolute top-12 right-5 flex items-center justify-center bg-white/95 rounded-l-2xl px-3 py-2 gap-2 z-10"
            style={{ borderTopLeftRadius: 28, borderBottomLeftRadius: 28 }}
          >
            <span
              className="text-xs font-medium whitespace-nowrap"
              style={{ color: staffInfo?.work_status ? "#27C230" : "#FF2525" }}
            >
              {staffInfo?.work_status ? "可接单" : "不接单"}
            </span>
            <button
              onClick={handleSwitchChange}
              className="relative w-10 h-5 rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0"
              style={{
                background: staffInfo?.work_status ? "#27C230" : "#ddd",
                padding: 2,
              }}
            >
              <span
                className="block w-[18px] h-[18px] rounded-full bg-white shadow transition-transform duration-200"
                style={{
                  transform: staffInfo?.work_status
                    ? "translateX(20px)"
                    : "translateX(0)",
                }}
              />
            </button>
          </div>
        )}

        {/* 用户信息卡片 */}
        {!loading && (
          <div className="flex items-center">
            <img
              src={staffInfo?.thumb || ""}
              alt=""
              className="w-[60px] h-[60px] rounded-full object-cover mr-3"
              style={{ border: "3px solid rgba(255,255,255,0.4)" }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = "";
              }}
            />
            <div className="flex-1">
              <div className="flex items-center mb-1">
                <span className="text-lg font-bold mr-2" style={{ color: "#000" }}>
                  {staffInfo?.name || "--"}
                </span>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className={
                        (staffInfo?.score ?? 0) >= star
                          ? "ri-star-fill"
                          : "ri-star-line"
                      }
                      style={{ fontSize: 16, color: "#FF9500" }}
                    />
                  ))}
                </div>
                <span className="text-sm ml-0.5" style={{ color: "#FF9500", fontWeight: 600 }}>
                  {staffInfo?.score || "0.0"}
                </span>
              </div>
              <span className="text-xs" style={{ color: "#000" }}>
                评分 {staffInfo?.score || "0.0"}
              </span>
            </div>
          </div>
        )}

        {/* 加载骨架屏 */}
        {loading && (
          <div className="flex items-center">
            <div
              className="w-[60px] h-[60px] rounded-full animate-pulse mr-3"
              style={{ background: "rgba(255,255,255,0.15)" }}
            />
            <div className="flex-1 space-y-2">
              <div
                className="h-5 w-24 rounded animate-pulse"
                style={{ background: "rgba(255,255,255,0.15)" }}
              />
              <div
                className="h-3 w-16 rounded animate-pulse"
                style={{ background: "rgba(255,255,255,0.1)" }}
              />
            </div>
          </div>
        )}
      </div>

      {/* 内容区 */}
      <div className="px-4 -mt-4 relative z-10 space-y-4">
        {/* 当前位置卡片 — 点击刷新定位 */}
        <button
          onClick={fetchAndUpdateLocation}
          className="flex items-center gap-3 p-4 rounded-2xl active:opacity-70 transition-opacity w-full text-left"
          style={{
            background: "#FAFBFF",
            border: "2px solid rgba(255,255,255,0.7)",
          }}
        >
          <i
            className={`ri-map-pin-line ${locating ? "animate-pulse" : ""}`}
            style={{ fontSize: 26, color: BRAND }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center text-sm text-gray-800 truncate">
              <span className="mr-1">当前位置:</span>
              {locating ? (
                <span className="text-gray-400">定位中...</span>
              ) : (
                <span className="font-semibold truncate">
                  {staffInfo?.current_address || "点击获取位置"}
                </span>
              )}
            </div>
            {staffInfo?.current_time_desc && !locating && (
              <div className="text-xs text-gray-500 mt-1">
                {staffInfo.current_time_desc}更新
              </div>
            )}
            {locating && (
              <div className="text-xs mt-1" style={{ color: BRAND }}>
                正在获取 GPS 定位...
              </div>
            )}
          </div>
          <i className="ri-refresh-line text-gray-400" style={{ fontSize: 18 }} />
        </button>

        {/* 综合数据卡片 */}
        <div className="bg-white rounded-2xl p-4">
          <div className="text-base font-bold text-gray-900 mb-3">综合数据</div>

          {/* 今日佣金横条 */}
          <div
            className="p-3 rounded-xl mb-3"
            style={{
              background: "linear-gradient(52deg, #fe7a3a99, transparent 82%)",
            }}
          >
            <button
              onClick={() => navigate("/workspace/money/capital")}
              className="flex items-baseline gap-2"
            >
              <span className="text-[#FE7A3A] font-bold text-2xl">
                ¥{staffInfo?.today_price ?? "0.00"}
              </span>
              <div className="flex items-center text-xs text-gray-500">
                今日获得佣金
                <i className="ri-arrow-right-s-line ml-0.5" />
              </div>
            </button>
          </div>

          {/* 8宫格统计 */}
          <div className="grid grid-cols-4 gap-y-3 py-3">
            {girdData.map((item, i) => {
              const isClickable = item.key === "today_order";
              return (
                <button
                  key={item.key}
                  onClick={
                    isClickable ? () => navigate("/workspace/order") : undefined
                  }
                  className={`flex flex-col items-center justify-center ${isClickable ? "active:opacity-70 cursor-pointer" : ""}`}
                >
                  <span className="text-lg font-bold text-gray-900 mb-1">
                    {loading ? "--" : (staffInfo?.[item.key] ?? 0)}
                  </span>
                  <span className="text-xs text-gray-500">{item.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 其他服务 - 大菜单卡片区 */}
        <div className="bg-white rounded-2xl p-4">
          <div className="text-base font-bold text-gray-900 mb-4">其他服务</div>

          {/* 大卡片 */}
          {(config?.staff_home_large_menu ?? []).map((item, i) => (
              <button
              key={`lg-${i}`}
              onClick={() =>
                item.url &&
                navigate(resolveUrl(item.url))
              }
              className="flex-1 h-[80px] relative flex items-center rounded-2xl overflow-hidden w-full mb-3 active:scale-[0.98] transition-transform"
            >
              <img
                src={item.pic}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <span className="ml-4 text-sm font-bold text-gray-900 relative z-10">
                {item.name}
              </span>
            </button>
          ))}

          {/* 小图标网格 */}
          <div className="grid grid-cols-4 mt-4">
            {(config?.staff_home_menu ?? []).map((item, i) => (
              <button
                key={`sm-${i}`}
                onClick={() =>
                  item.url &&
                  navigate(resolveUrl(item.url))
                }
                className="flex flex-col items-center justify-center py-2 active:opacity-70 transition-opacity"
              >
                <img
                  src={item.pic}
                  alt=""
                  className="w-[50px] h-[50px] object-contain mb-1"
                />
                <span className="text-xs text-gray-700">{item.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 底部导航栏（动态读取 /staffapi/config 的 staff_navigation_bar） */}
      <BottomNav />
    </div>
  );
}
